import { DocumentText1, Gallery, Star1, UserSquare, VideoVertical } from 'iconsax-react';
import { forwardRef, type PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import CameraIcon from '@/assets/images/icons/camera.svg?react';
import { MediaCaptureMethodPicker } from '@/components/common/media-capture-method-picker';
import useConversationStore from '@/hooks/conversations/use-conversation-store';
import useUserPlatform, { useUserHints } from '@/hooks/use-user-platform';
import ROUTES from '@/routes';
import { FileType } from '@/utils/file-type';
import cn from '@/utils/merge-class-names';

interface ConversationAttachmentMenuProps {
  isOpen: boolean;
  disabled?: boolean;
  onPick: (type: FileType, capture?: 'camera' | 'album') => void;
  options?: ('camera' | 'album' | 'image' | 'video' | 'contact' | 'file' | 'collection')[];
}

const ItemDescription = ({ children }: PropsWithChildren) => (
  <div className='relative flex justify-center h-5'>
    <span className='absolute text-body2 glyph-latin:!text-body3 desktop:text-sm  text-secondary font-normal'>
      {children}
    </span>
  </div>
);

const ConversationAttachmentMenu = forwardRef<HTMLDivElement, ConversationAttachmentMenuProps>(
  (
    { isOpen, disabled, onPick, options = ['camera', 'album', 'image', 'video', 'contact', 'file', 'collection'] },
    ref,
  ) => {
    const { t } = useTranslation();
    const { isIOS, isDesktop } = useUserPlatform();
    const {
      hints: { osType },
    } = useUserHints();
    const { type, id } = useConversationStore();

    const parentPath = type === 'session' ? ROUTES.MESSAGES.SESSIONS : ROUTES.MESSAGES.GROUPS;

    return (
      <div
        ref={isOpen ? ref : null}
        className={cn(
          'grid w-full auto-cols-auto min-h-16 grid-flow-col-dense grid-rows-1 justify-evenly gap-2 px-5 whitespace-nowrap overflow-x-auto max-w-100dvw',
          'desktop:absolute desktop:bottom-full desktop:w-fit desktop:bg-neutral-0 desktop:p-4 desktop:shadow-toast desktop:rounded-lg desktop:mb-px desktop:z-30',
          {
            hidden: !isOpen,
          },
        )}>
        {options.includes('camera') && !(isIOS || isDesktop || osType === 'desktop') && (
          <MediaCaptureMethodPicker
            disabled={disabled}
            onPick={onPick}>
            <div className='flex flex-col items-center gap-1'>
              <CameraIcon className='size-6' />
              <ItemDescription>{t('conversation.attachment-menu.camera')}</ItemDescription>
            </div>
          </MediaCaptureMethodPicker>
        )}

        {options.includes('image') && (
          <button
            type='button'
            disabled={disabled}
            className='btn btn-ghost btn-sm h-fit flex flex-col items-center gap-1'
            onClick={() => onPick(FileType.Image)}>
            <Gallery className='size-6' />
            <ItemDescription>{t('conversation.attachment-menu.image')}</ItemDescription>
          </button>
        )}

        {options.includes('video') && (
          <button
            type='button'
            disabled={disabled}
            className='btn btn-ghost btn-sm h-fit flex flex-col items-center gap-1'
            onClick={() => onPick(FileType.Video)}>
            <VideoVertical className='size-6' />
            <ItemDescription>{t('conversation.attachment-menu.video')}</ItemDescription>
          </button>
        )}

        {options.includes('contact') && (
          <Link
            className='btn btn-ghost btn-sm h-fit flex flex-col items-center gap-1'
            to={parentPath.CONTACT.$buildPath({
              params: { id },
            })}>
            <UserSquare className='size-6' />
            <ItemDescription>{t('conversation.attachment-menu.contact')}</ItemDescription>
          </Link>
        )}

        {options.includes('file') && (
          <button
            type='button'
            disabled={disabled}
            className='btn btn-ghost btn-sm h-fit flex flex-col items-center gap-1'
            onClick={() => onPick(FileType.File)}>
            <DocumentText1 className='size-6' />
            <ItemDescription>{t('conversation.attachment-menu.file')}</ItemDescription>
          </button>
        )}
        {options.includes('collection') && (
          <Link
            className='btn btn-ghost btn-sm h-fit flex flex-col items-center gap-1'
            to={parentPath.COLLECTIONS.$buildPath({
              params: { id },
            })}>
            <Star1 className='size-6' />
            <ItemDescription>{t('conversation.attachment-menu.collection')}</ItemDescription>
          </Link>
        )}
      </div>
    );
  },
);

ConversationAttachmentMenu.displayName = 'ConversationAttachmentMenu';

export default ConversationAttachmentMenu;
